/**
 * Shared error handling utilities for DFP plugins
 * Provides consistent error handling across frontend and backend
 */

/**
 * Standardized error response interface
 * Matches the format used in dfp-backend
 */
export interface DfpErrorResponse {
  error: string;
  message: string;
  details?: string;
  timestamp?: string;
}

/**
 * Error types for categorization
 */
export enum DfpErrorType {
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  REQUEST_TIMEOUT = 'REQUEST_TIMEOUT',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Create a standardized error response
 * @param error - Error type or code
 * @param message - Human-readable error message
 * @param details - Optional additional details
 * @returns Standardized error response object
 */
export function createErrorResponse(
  error: string | DfpErrorType,
  message: string,
  details?: string
): DfpErrorResponse {
  return {
    error: typeof error === 'string' ? error : error,
    message,
    details,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Parse error from response text
 * Handles both JSON and plain text error responses
 * @param errorText - Error text from response
 * @returns Parsed error object or null if parsing fails
 */
export function parseErrorResponse(errorText: string): DfpErrorResponse | null {
  if (!errorText || errorText.trim() === '') {
    return null;
  }

  try {
    const parsed = JSON.parse(errorText);
    // Check if it's already in the correct format
    if (parsed.error && parsed.message) {
      return {
        ...parsed,
        timestamp: parsed.timestamp || new Date().toISOString(),
      };
    }
    // Try to extract error from different formats
    return createErrorResponse(
      parsed.error || parsed.code || DfpErrorType.UNKNOWN_ERROR,
      parsed.message || parsed.error || errorText,
      parsed.details || parsed.reason
    );
  } catch {
    // If parsing fails, assume it's plain text
    return createErrorResponse(
      DfpErrorType.UNKNOWN_ERROR,
      errorText,
      undefined
    );
  }
}

/**
 * Extract error message from various error formats
 * Handles Error objects, JSON strings, and plain strings
 * @param error - Error to extract message from
 * @returns Extracted error message
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message;
    // Try to parse if it's a JSON string
    try {
      const parsed = JSON.parse(message);
      return parsed.message || parsed.error || message;
    } catch {
      return message;
    }
  }

  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      return parsed.message || parsed.error || error;
    } catch {
      return error;
    }
  }

  return 'An unknown error occurred';
}

/**
 * Check if error is a network error
 * @param error - Error to check
 * @returns True if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return error.message.includes('fetch') || error.message.includes('network');
  }
  if (error instanceof Error) {
    return error.message.toLowerCase().includes('network') ||
           error.message.toLowerCase().includes('fetch') ||
           error.message.toLowerCase().includes('connection');
  }
  return false;
}

/**
 * Check if error is a timeout error
 * @param error - Error to check
 * @returns True if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message.toLowerCase().includes('timeout') ||
           error.message.toLowerCase().includes('timed out') ||
           error.name === 'AbortError';
  }
  return false;
}

/**
 * Create error response from HTTP status code
 * @param status - HTTP status code
 * @param message - Optional custom message
 * @returns Error response
 */
export function createErrorFromStatus(status: number, message?: string): DfpErrorResponse {
  let errorType: DfpErrorType;
  let defaultMessage: string;

  switch (status) {
    case 400:
      errorType = DfpErrorType.BAD_REQUEST;
      defaultMessage = 'Bad request';
      break;
    case 401:
      errorType = DfpErrorType.UNAUTHORIZED;
      defaultMessage = 'Unauthorized';
      break;
    case 403:
      errorType = DfpErrorType.FORBIDDEN;
      defaultMessage = 'Forbidden';
      break;
    case 404:
      errorType = DfpErrorType.NOT_FOUND;
      defaultMessage = 'Resource not found';
      break;
    case 408:
      errorType = DfpErrorType.REQUEST_TIMEOUT;
      defaultMessage = 'Request timeout';
      break;
    case 503:
      errorType = DfpErrorType.SERVICE_UNAVAILABLE;
      defaultMessage = 'Service unavailable';
      break;
    default:
      if (status >= 500) {
        errorType = DfpErrorType.INTERNAL_ERROR;
        defaultMessage = 'Internal server error';
      } else {
        errorType = DfpErrorType.UNKNOWN_ERROR;
        defaultMessage = 'An error occurred';
      }
  }

  return createErrorResponse(
    errorType,
    message || defaultMessage,
    `HTTP ${status}`
  );
}

/**
 * Check if error indicates service unavailable
 * Detects various patterns that indicate DFP service is unavailable
 * @param error - Error to check (string, Error object, or unknown)
 * @returns True if error indicates service unavailable
 */
export function isServiceUnavailableError(error: string | Error | unknown): boolean {
  const errorString = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  const lowerError = errorString.toLowerCase();
  
  return (
    lowerError.includes('dfp service') ||
    lowerError.includes('unavailable') ||
    lowerError.includes('econnrefused') ||
    lowerError.includes('503') ||
    lowerError.includes('service unavailable') ||
    lowerError.includes('connection refused')
  );
}

